import React, { useCallback, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Box,
  Button,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import {
  useNotifications,
  useUnreadCount,
  useNotificationRealtime,
} from "@domains/notification/hooks/useNotifications";
import type {
  NotificationItem,
  NotificationType,
} from "@domains/notification/types";
import { PROJECTS } from "src/platform/constants/routes";
import { ALPHA } from "src/shell/styles/designTokens";

interface NotificationsMenuProps {
  showLabel?: boolean;
}

/** Format an ISO timestamp as a short relative string ("2m ago", "1h ago"). */
function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getNotificationIcon(type: NotificationType): React.ReactElement {
  switch (type) {
    case "provisioned":
      return <CheckCircleIcon fontSize="small" color="success" />;
    case "failed":
      return <ErrorIcon fontSize="small" color="error" />;
    case "deprovisioned":
    case "info":
    default:
      return <InfoIcon fontSize="small" color="info" />;
  }
}

const NotificationsMenu: React.FC<NotificationsMenuProps> = ({
  showLabel = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const { notifications, loading, markRead, markAllRead, reload } =
    useNotifications();
  const { count, reload: reloadCount } = useUnreadCount();

  // When a realtime notification arrives, refresh the list and the badge.
  const handleNewNotification = useCallback(() => {
    void reload();
    void reloadCount();
  }, [reload, reloadCount]);

  useNotificationRealtime(handleNewNotification);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllRead = useCallback(async () => {
    await markAllRead();
    void reloadCount();
  }, [markAllRead, reloadCount]);

  const handleNotificationClick = useCallback(
    async (notification: NotificationItem) => {
      if (!notification.read) {
        await markRead(notification.id);
        void reloadCount();
      }
      handleClose();
      if (notification.resourceAlias) {
        const orgAlias = orgSessionManager.getOrgAlias();
        // We don't have project context from a global menu; route to the
        // org-scoped resources listing so the user can find the resource.
        if (orgAlias) {
          void navigate(PROJECTS.ROOT.getPath(orgAlias));
        }
      }
    },
    [markRead, reloadCount, navigate],
  );

  const hoverSx = {
    color: "text.secondary",
    "&:hover": {
      color: "primary.main",
      backgroundColor: alpha(theme.palette.primary.main, ALPHA.NAV_ACTIVE_BG),
    },
    borderRadius: 1,
    transition: "color 0.2s ease, background-color 0.2s ease",
  };

  const trigger = showLabel ? (
    <Box
      onClick={handleOpen}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        height: 40,
        px: 2,
        cursor: "pointer",
        ...hoverSx,
      }}
    >
      <Badge badgeContent={count} color="error" invisible={count === 0}>
        <NotificationsNoneIcon fontSize="small" />
      </Badge>
      <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
        Notifications
      </Typography>
    </Box>
  ) : (
    <Tooltip title="Notifications">
      <IconButton
        onClick={handleOpen}
        aria-controls={open ? "notifications-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={hoverSx}
      >
        <Badge badgeContent={count} color="error" invisible={count === 0}>
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      {trigger}

      <Menu
        anchorEl={anchorEl}
        id="notifications-menu"
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 360,
            maxHeight: 480,
            borderRadius: 1,
            boxShadow: theme.shadows[4],
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
          {count > 0 && (
            <Button
              size="small"
              onClick={() => void handleMarkAllRead()}
              sx={{ textTransform: "none" }}
            >
              Mark all as read
            </Button>
          )}
        </Box>
        <Divider />

        {loading && notifications.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <NotificationsNoneIcon
              sx={{ fontSize: 36, color: "text.disabled", mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          notifications.map((n) => (
            <MenuItem
              key={n.id}
              onClick={() => void handleNotificationClick(n)}
              sx={{
                px: 2,
                py: 1.5,
                alignItems: "flex-start",
                gap: 1.5,
                "&:hover": { bgcolor: "action.hover" },
                position: "relative",
              }}
            >
              <Box sx={{ mt: 0.25 }}>{getNotificationIcon(n.type)}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={n.read ? 400 : 600}
                  sx={{ wordBreak: "break-word" }}
                >
                  {n.title}
                </Typography>
                {n.body && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {n.body}
                  </Typography>
                )}
                <Typography variant="caption" color="text.disabled">
                  {formatRelativeTime(n.createdAt)}
                </Typography>
              </Box>
              {!n.read && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    mt: 0.5,
                    flexShrink: 0,
                  }}
                  aria-label="unread"
                />
              )}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default NotificationsMenu;
